import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from '../../../../store/app.state';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { selectAllSubAccounts } from '../../../../store/sub-accounts/sub-accounts.selectors';
import {
  LucideAngularModule,
  Plus,
  Eye,
  FileText,
  Download,
  Bookmark,
  BookmarkCheck,
  MoreVertical,
} from 'lucide-angular';
import { ModalService } from '../../../../core/services/modal.service';
import { ToastService } from '../../../../core/services/toast.service';
import { AddDocumentModalComponent } from '../../../../shared/components/modal/add-document-modal';
import { DocumentViewerModalComponent } from '../../../../shared/components/modal/document-viewer-modal';
import { PassportService } from '../../../../core/services/passport.service';
import { DocumentService } from '../../../../core/services/document.service';
import { Prestation } from '../../../../core/interfaces/prestation.interface';
import { PrestationDetailsModalComponent } from '../../../../shared/components/modal/prestation-details-modal';
import { PrestationService } from '../../../../core/services/prestation.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
})
export class DocumentsComponent implements OnInit, OnDestroy {
  readonly Plus = Plus;
  readonly Eye = Eye;
  readonly Download = Download;
  readonly Bookmark = Bookmark;
  readonly FileText = FileText;
  readonly BookmarkCheck = BookmarkCheck;
  readonly MoreVertical = MoreVertical;
  private subscription = new Subscription();

  // Inputs pour les bénéficiaires
  @Input() beneficiaries: Array<{ fullname: string; passportNumber: string }> =
    [];
  @Input() showAddButton: boolean = true;
  @Input() contractId?: string | null; // ID du contrat optionnel

  // Propriétés internes
  loading = false;
  currentUser: any = null;
  subAccounts: any[] = [];
  documentTypes: any[] = [];
  documentTypesLoading = false;
  documentsByPassport: any[] = [];
  documentsLoading = false;
  documentsError: string | null = null;
  searchTerm: string = '';
  showFavoritesOnly: boolean = false;

  selectedPassport: string = '';

  // DaisyUI dropdown auto-closes; no explicit state needed
  // Loading indicator for viewing prestation
  loadingPrestationForId: string | null = null;

  // Propriétés de pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasMoreDocuments = false;

  // Getter pour les documents filtrés
  get filteredDocuments(): any[] {
    if (!this.searchTerm.trim()) {
      return this.documentsByPassport;
    }

    const searchLower = this.searchTerm.toLowerCase().trim();
    return this.documentsByPassport.filter((doc) => {
      // Recherche dans le nom du fichier
      if (doc.filename && doc.filename.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Recherche dans le type de document
      if (
        doc.documentType?.label &&
        doc.documentType.label.toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      return false;
    });
  }

  constructor(
    private store: Store<AppState>,
    private modalService: ModalService,
    private toastService: ToastService,
    private passportService: PassportService,
    private documentService: DocumentService,
    private prestationService: PrestationService
  ) { }

  ngOnInit(): void {
    console.log('DocumentsComponent: ngOnInit - Surveillance du store...');

    // Récupérer les types de documents
    this.loadDocumentTypes();

    // Surveiller l'utilisateur courant
    this.subscription.add(
      this.store.select(selectCurrentUser).subscribe((currentUser) => {
        console.log(
          'DocumentsComponent: Utilisateur courant récupéré du store:',
          currentUser
        );
        this.currentUser = currentUser;
        this.selectedPassport = currentUser?.passportNumber || '';
      })
    );

    // Surveiller les sous-comptes
    this.subscription.add(
      this.store.select(selectAllSubAccounts).subscribe((subAccounts) => {
        console.log(
          'DocumentsComponent: Sous-comptes récupérés du store:',
          subAccounts
        );
        this.subAccounts = subAccounts || [];
      })
    );

    // Écouter les événements du modal
    this.subscription.add(
      this.modalService.getEvents().subscribe((event) => {
        if (event.type === 'documentAdded') {
          console.log('DocumentsComponent: Document ajouté:', event.data);
          // Fermer le modal
          this.modalService.closeAll();
          // Actualiser la liste des documents
          if (this.selectedPassport) {
            this.refreshDocuments();
          }
          this.toastService.success('Document ajouté avec succès !');
        }
      })
    );

    // Charger les documents pour le passeport sélectionné au démarrage
    if (this.selectedPassport) {
      this.loadDocumentsByPassport(this.selectedPassport);
    }
  }

  ngOnDestroy(): void {
    // Nettoyer les souscriptions pour éviter les fuites mémoire
    this.subscription.unsubscribe();
    console.log('DocumentsComponent: Souscriptions nettoyées');
  }

  loadDocumentTypes(): void {
    this.documentTypesLoading = true;
    console.log('DocumentsComponent: Chargement des types de documents...');

    this.subscription.add(
      this.passportService.getAllDocumentTypes().subscribe({
        next: (response) => {
          console.log(
            'DocumentsComponent: Types de documents récupérés:',
            response
          );
          this.documentTypes = response.data || response || [];
          this.documentTypesLoading = false;
        },
        error: (error) => {
          console.error(
            'DocumentsComponent: Erreur lors du chargement des types de documents:',
            error
          );
          this.documentTypesLoading = false;
          // Fallback vers les types par défaut en cas d'erreur
          this.documentTypes = [
            { value: 'prescription', label: 'Prescription médicale' },
            { value: 'ordonnance', label: 'Ordonnance' },
            { value: 'certificat', label: 'Certificat médical' },
            { value: 'analyse', label: "Résultats d'analyse" },
            { value: 'radio', label: 'Radiographie' },
            { value: 'scanner', label: 'Scanner' },
            { value: 'irm', label: 'IRM' },
            { value: 'echographie', label: 'Échographie' },
            { value: 'autre', label: 'Autre document' },
          ];
        },
      })
    );
  }

  selectPassport(passportNumber: string) {
    this.selectedPassport = passportNumber;
    this.loadDocumentsByPassport(passportNumber);
  }

  loadDocumentsByPassport(passportNumber: string, page: number = 1) {
    this.documentsLoading = true;
    this.documentsError = null;
    this.currentPage = page;

    // Paramètres de pagination et filtre
    const params = {
      page: this.currentPage,
      limit: this.pageSize,
      favoritesOnly: this.showFavoritesOnly,
    };

    // Appeler le service avec l'ID du contrat et les paramètres de pagination
    this.documentService
      .getDocumentsByPassportNumber(passportNumber, this.contractId, params)
      .subscribe({
        next: (response) => {
          // L'API retourne { documents: [], pagination: {} }
          this.documentsByPassport =
            response?.documents || response?.data || [];

          // Mettre à jour les informations de pagination
          if (response?.pagination) {
            this.totalItems =
              response.pagination.totalItems || response.pagination.total || 0;
            this.totalPages =
              response.pagination.totalPages ||
              response.pagination.pageCount ||
              0;
            this.hasMoreDocuments = this.currentPage < this.totalPages;
          } else {
            // Fallback si pas de pagination dans la réponse
            this.totalItems = this.documentsByPassport.length;
            this.totalPages = 1;
            this.hasMoreDocuments = false;
          }

          this.documentsLoading = false;
        },
        error: (err) => {
          // this.documentsError = 'Erreur lors du chargement des documents.';
          this.documentsLoading = false;
          this.documentsByPassport = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.hasMoreDocuments = false;
        },
      });
  }

  addDocument(): void {
    console.log("DocumentsComponent: Ouverture du modal d'ajout de document");
    console.log(
      'DocumentsComponent: Types de documents disponibles:',
      this.documentTypes
    );
    console.log(
      'DocumentsComponent: Bénéficiaires disponibles:',
      this.beneficiaries
    );
    console.log('DocumentsComponent: Utilisateur courant:', this.currentUser);
    console.log(
      'DocumentsComponent: Sous-comptes disponibles:',
      this.subAccounts
    );

    this.modalService.open({
      component: AddDocumentModalComponent,
      title: 'Ajouter un document',
      width: '500px',
      closable: true,
      data: {
        documentTypes: this.documentTypes,
        beneficiaries: this.beneficiaries,
        currentUser: this.currentUser,
        subAccounts: this.subAccounts,
      },
    });
  }

  onDownloadDocument(doc: any) {
    console.log('DocumentsComponent: Téléchargement du document:', doc);

    if (!doc.url) {
      console.error('DocumentsComponent: URL du document manquante');
      this.toastService.error('URL du document manquante');
      return;
    }

    // Obtenir l'URL presignée pour le document
    this.documentService.getPresignedUrl(doc.url).subscribe({
      next: (response) => {
        console.log('DocumentsComponent: URL presignée obtenue:', response.url);

        // Télécharger le fichier en utilisant fetch pour forcer le téléchargement
        this.downloadFile(response.url, doc.filename || 'document');
      },
      error: (error) => {
        console.error(
          "DocumentsComponent: Erreur lors de l'obtention de l'URL presignée:",
          error
        );
        this.toastService.error('Erreur lors du téléchargement du document');
      },
    });
  }

  private downloadFile(url: string, filename: string) {
    // Afficher un indicateur de chargement
    this.toastService.info('Téléchargement en cours...');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        // Créer un blob URL pour le téléchargement
        const blobUrl = window.URL.createObjectURL(blob);

        // Créer un lien temporaire pour télécharger le document
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';

        // Ajouter le lien au DOM, cliquer dessus, puis le supprimer
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Nettoyer le blob URL
        window.URL.revokeObjectURL(blobUrl);

        // Afficher un message de succès
        this.toastService.success('Document téléchargé avec succès !');
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement:', error);
        this.toastService.error('Erreur lors du téléchargement du document');
      });
  }

  onViewDocument(doc: any) {
    console.log('DocumentsComponent: Visualisation du document:', doc);

    if (!doc.url) {
      console.error('DocumentsComponent: URL du document manquante');
      this.toastService.error('URL du document manquante');
      return;
    }

    // Obtenir l'URL presignée pour le document
    this.documentService.getPresignedUrl(doc.url).subscribe({
      next: (response) => {
        console.log('DocumentsComponent: URL presignée obtenue:', response.url);

        window.open(response.url, '_blank');
        // Ouvrir le modal avec le document
        // this.modalService.open({
        //   component: DocumentViewerModalComponent,
        //   title: doc.filename || 'Visualisation du document',
        //   width: '800px',
        //   closable: true,
        //   data: {
        //     document: doc,
        //     documentUrl: response.url,
        //   },
        // });
      },
      error: (error) => {
        console.error(
          "DocumentsComponent: Erreur lors de l'obtention de l'URL presignée:",
          error
        );
        this.toastService.error('Erreur lors du chargement du document');
      },
    });
  }

  toggleFavorite(doc: any) {
    console.log('DocumentsComponent: Toggle favoris pour le document:', doc);

    // Initialiser isFavorite si elle n'existe pas
    if (doc.isFavorite === undefined) {
      doc.isFavorite = false;
    }

    // Sauvegarder l'état précédent pour rollback en cas d'erreur
    const previousState = doc.isFavorite;

    // Toggle l'état du favori localement pour l'UI
    doc.isFavorite = !doc.isFavorite;

    // Appeler l'API pour sauvegarder l'état
    this.documentService.toggleFavorite(doc._id, doc.isFavorite).subscribe({
      next: (response) => {
        console.log(
          'DocumentsComponent: État favori mis à jour avec succès:',
          response
        );
        const message = doc.isFavorite
          ? 'Document ajouté aux favoris'
          : 'Document retiré des favoris';
        this.toastService.success(message);
      },
      error: (error) => {
        console.error(
          'DocumentsComponent: Erreur lors de la mise à jour des favoris:',
          error
        );
        // Rollback l'état en cas d'erreur
        doc.isFavorite = previousState;
        this.toastService.error('Erreur lors de la mise à jour des favoris');
      },
    });
  }

  // No dropdown helpers required with DaisyUI
  toggleFavoritesFilter(): void {
    this.showFavoritesOnly = !this.showFavoritesOnly;
    console.log(
      'DocumentsComponent: Filtre favoris:',
      this.showFavoritesOnly ? 'activé' : 'désactivé'
    );

    // Recharger les documents avec le nouveau filtre
    if (this.selectedPassport) {
      this.loadDocumentsByPassport(this.selectedPassport, 1);
    }
  }

  viewPrestation(prestationId: string, docId?: string): void {
    this.loadingPrestationForId = docId || null;
    this.prestationService
      .getPrestationById(prestationId)
      .pipe(
        finalize(() => {
          this.loadingPrestationForId = null;
        })
      )
      .subscribe({
        next: (response) => {
          console.log('DocumentsComponent: Prestation récupérée:', response);
          this.viewPrestationDetails(response);
        },
        error: (error) => {
          console.error(
            'DocumentsComponent: Erreur lors de la récupération de la prestation:',
            error
          );
          this.toastService.error('Erreur lors du chargement de la prestation');
        },
      });
  }

  viewPrestationDetails(prestation: Prestation): void {
    console.log(
      'MedicalHistoryComponent: Affichage des détails de la prestation:',
      prestation
    );

    // Vérifier que le service modal est disponible
    if (!this.modalService) {
      console.error('MedicalHistoryComponent: ModalService non disponible');
      alert('Erreur: Service modal non disponible');
      return;
    }

    console.log(
      'MedicalHistoryComponent: ModalService disponible:',
      this.modalService
    );
    const modalId = this.modalService.open({
      component: PrestationDetailsModalComponent,
      title: `Détails de la prestation ${prestation.costumId}`,
      width: '80vw',
      closable: true,
      data: {
        prestation: prestation,
      },
    });
    this.handlePrestationModalEvents(modalId);
  }

  handlePrestationModalEvents(modalId: string): void {
    console.log('MedicalHistoryComponent: Événement reçu:', modalId);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  // Méthodes de pagination
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.loadDocumentsByPassport(this.selectedPassport, page);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  getPaginationArray(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      this.currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  // Méthode pour recharger les documents après ajout
  refreshDocuments(): void {
    if (this.selectedPassport) {
      this.loadDocumentsByPassport(this.selectedPassport, this.currentPage);
    }
  }

  // Pour *ngFor trackBy
  trackById(index: number, item: any) {
    return item?._id || item?.id;
  }
  trackByPassportNumber(index: number, item: any) {
    return item.passportNumber;
  }

  // Méthode pour calculer le nombre maximum d'éléments sur la page courante
  getMaxItemsOnPage(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }
}
